import customerRiskTables from "./customerRiskTables.json";

interface CustomerRiskTable {
    simYears: number;
    availableCustomerYears: number[];
    defaultCustomerYears: number;
    generalAssetRisk: number;
    deathRisks: number[];
    itpRisks: number[];
}

export function getCustomerRiskTable(
    simulationYears: number
): CustomerRiskTable {
    const tables = customerRiskTables as CustomerRiskTable[];

    // Cerca il valore più alto <= simulationYears
    const matchingTable = tables
        .filter((table) => table.simYears <= simulationYears)
        .reduce<CustomerRiskTable | undefined>(
            (best, table) =>
                !best || table.simYears > best.simYears
                    ? table
                    : best,
            undefined
        );

    if (matchingTable) {
        return matchingTable;
    }

    // Se simulationYears è inferiore a tutti,
    // restituisce comunque la tabella con simYears minimo
    return tables.reduce((min, table) =>
        table.simYears < min.simYears ? table : min
    );
}

export interface ProbabilityFraction {
    numerator: number;
    denominator: number;
}

export function percentageToFraction(
    percentage: number
): ProbabilityFraction {
    if (!Number.isFinite(percentage) || percentage < 0) {
        throw new Error("Percentage must be a finite number >= 0");
    }

    if (percentage === 0) {
        return {
            numerator: 0,
            denominator: 1,
        };
    }

    if (percentage >= 100) {
        return {
            numerator: 1,
            denominator: 1,
        };
    }

    const probability = percentage / 100;

    /*
     * Denominatori che risultano immediatamente comprensibili
     * all'utente.
     */
    const denominators = [
        2,
        3,
        4,
        5,
        6,
        8,
        10,
        12,
        15,
        20,
        25,
        30,
        40,
        50,
        60,
        75,
        100,
        150,
        200,
        250,
        500,
        1000,
    ];

    let best: ProbabilityFraction | null = null;
    let bestScore = Infinity;

    for (const denominator of denominators) {
        const exactNumerator = probability * denominator;

        /*
         * Proviamo il numeratore arrotondato.
         */
        const numerator = Math.round(exactNumerator);

        if (numerator < 1 || numerator > denominator) {
            continue;
        }

        const representedProbability = numerator / denominator;

        /*
         * Errore relativo rispetto alla probabilità reale.
         */
        const relativeError =
            Math.abs(representedProbability - probability) /
            probability;

        /*
         * Penalità per numeratori grandi.
         *
         * "1 su 20" è più immediato di "5 su 100".
         */
        const numeratorPenalty =
            numerator === 1
                ? 0
                : Math.log10(numerator) * 0.12;

        /*
         * Penalità molto leggera per denominatori grandi.
         */
        const denominatorPenalty =
            Math.log10(denominator) * 0.015;

        /*
         * Bonus per alcune frazioni particolarmente intuitive.
         */
        const simpleFractionBonus =
            numerator === 1
                ? -0.08
                : 0;

        const score =
            relativeError +
            numeratorPenalty +
            denominatorPenalty +
            simpleFractionBonus;

        if (score < bestScore) {
            bestScore = score;

            best = {
                numerator,
                denominator,
            };
        }
    }

    /*
     * Questo non dovrebbe mai verificarsi nel range previsto,
     * ma garantisce comunque sempre un risultato.
     */
    if (!best) {
        return {
            numerator: 1,
            denominator: Math.round(1 / probability),
        };
    }

    /*
     * Riduzione della frazione.
     *
     * Es. 5/100 → 1/20
     */
    const gcd = greatestCommonDivisor(
        best.numerator,
        best.denominator
    );

    return {
        numerator: best.numerator / gcd,
        denominator: best.denominator / gcd,
    };
}

function greatestCommonDivisor(
    a: number,
    b: number
): number {
    a = Math.abs(a);
    b = Math.abs(b);

    while (b !== 0) {
        const temp = b;
        b = a % b;
        a = temp;
    }

    return a;
}

export function calcThreeEventProbability(event1: number, event2: number, event3: number) {
    const not1 = 1 - (event1 / 100);
    const not2 = 1 - (event2 / 100);
    const not3 = 1 - (event3 / 100);
    const neitherOfThree = not1 * not2 * not3;
    return 1 - neitherOfThree;
}